import { AuthStatusMessage } from '@Auth/components/AuthStatusMessage';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { ErrorMessage } from '@Pages/pageRuntime/components/ui/ErrorMessage';
import {
  useTwoFactorCompletion,
  useTwoFactorTarget
} from '@Pages/pageRuntime/hooks/twoFactorFlows';
import { useTwoFactorBackupCodes } from '@Pages/pageRuntime/hooks/useTwoFactorBackupCodes';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { apiErrorMessage } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkTwoFactorBackupCodesHandle,
  SdkTwoFactorBackupCodesState
} from '@Pages/pageRuntime/sdk/types';
import { useState } from 'react';

import {
  TwoFactorFactorForm,
  TwoFactorLinkButton,
  TwoFactorLoadError,
  TwoFactorLoading
} from './TwoFactorPanels';
import { TwoFactorBackupCodeList, TwoFactorSendCodeButton } from './TwoFactorSendControls';

type TwoFactorBackupCodesProps = {
  /** The authenticatable resource. Defaults to the signed-in member's resource. */
  resourceId?: string;

  /** Additional classes for the container. */
  className?: string;

  /** Additional classes for the main buttons. */
  buttonClassName?: string;
};

type StepProps = {
  backupCodes: SdkTwoFactorBackupCodesHandle;
  state: SdkTwoFactorBackupCodesState;
  resourceId?: string;
  errors: string[];
  buttonClassName?: string;
};

/**
 * Backup codes for the signed-in member: how many unused codes remain, and a new set
 * after the member proves the second factor again with a new SMS code or an unused backup
 * code. The new codes show once, with Copy and Download. Every earlier code stops working.
 * It renders nothing for a visitor who is not signed in to a resource that enables
 * two-factor. Place it on an account settings page.
 *
 * @example
 * <Sdk.Components.TwoFactorBackupCodes />
 */
export function TwoFactorBackupCodes({
  resourceId,
  className,
  buttonClassName
}: TwoFactorBackupCodesProps) {
  const target = useTwoFactorTarget(resourceId);
  const afterSignIn = useTwoFactorCompletion(target.resourceId)?.next === 'backup_codes';
  const backupCodes = useTwoFactorBackupCodes({ resourceId });

  if (!backupCodes.isActive) {
    return null;
  }

  if (backupCodes.error) {
    return (
      <TwoFactorLoadError
        error={backupCodes.error}
        fallback="Your backup codes did not load. Reload the page."
        className={className}
      />
    );
  }

  const state = backupCodes.data;

  if (state === undefined) {
    return <TwoFactorLoading label="Loading backup codes" className={className} />;
  }

  const stepProps: StepProps = {
    backupCodes,
    state,
    resourceId,
    errors: backupCodes.actionError
      ? [apiErrorMessage(backupCodes.actionError, 'New codes were not generated.')]
      : [],
    buttonClassName
  };

  return (
    <div className={cn('grid gap-3', className)}>
      {backupCodes.codes ? (
        <>
          <p className="text-sm font-medium">Save your backup codes</p>
          <TwoFactorBackupCodeList codes={backupCodes.codes}>
            {afterSignIn ? (
              <Button
                type="button"
                className={cn('w-full', buttonClassName)}
                onClick={backupCodes.continue}
              >
                I saved my codes, continue
              </Button>
            ) : null}
          </TwoFactorBackupCodeList>
        </>
      ) : state.setupAvailable ? (
        <FirstSetStep {...stepProps} />
      ) : (
        <RegenerateStep {...stepProps} />
      )}
    </div>
  );
}

function FirstSetStep({ backupCodes, errors, buttonClassName }: StepProps) {
  return (
    <>
      <p className="text-sm">
        You do not have backup codes yet. Generate them now and keep them somewhere private.
      </p>
      <Button
        type="button"
        className={buttonClassName}
        disabled={backupCodes.isPending}
        onClick={() => void backupCodes.generate()}
      >
        Generate backup codes
      </Button>
      <ErrorMessage errors={errors} />
      <Button
        type="button"
        variant="link"
        className="text-muted-foreground"
        onClick={backupCodes.continue}
      >
        Skip for now
      </Button>
    </>
  );
}

function RegenerateStep({ backupCodes, state, resourceId, errors, buttonClassName }: StepProps) {
  const [usesBackupCode, setUsesBackupCode] = useState(false);
  const [value, setValue] = useState('');

  return (
    <>
      <p className="text-sm">
        {state.remainingCount === 1
          ? 'You have 1 unused backup code.'
          : `You have ${state.remainingCount} unused backup codes.`}{' '}
        New codes replace every current code.
      </p>
      {state.status ? (
        <AuthStatusMessage title="Code sent">{state.status}</AuthStatusMessage>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {usesBackupCode
          ? 'Confirm it is you with one of your current backup codes.'
          : `Confirm it is you with a code sent to the phone number ending ${state.maskedPhone}.`}
      </p>
      {usesBackupCode ? null : (
        <TwoFactorSendCodeButton flow="backupCodes" resourceId={resourceId} />
      )}
      {usesBackupCode || !state.smsConsentRequired ? (
        <TwoFactorFactorForm
          usesBackupCode={usesBackupCode}
          value={value}
          onChange={setValue}
          onSubmit={(factor) =>
            void backupCodes
              .generate(usesBackupCode ? { recoveryCode: factor } : { code: factor })
              .then((generated) => generated || setValue(''))
          }
          submitLabel="Generate new codes"
          errors={errors}
          disabled={backupCodes.isPending}
          buttonClassName={buttonClassName}
        />
      ) : null}
      <div className="flex">
        <TwoFactorLinkButton
          onClick={() => {
            setUsesBackupCode((current) => !current);
            setValue('');
          }}
        >
          {usesBackupCode ? 'Use a text message code instead' : 'Use a backup code instead'}
        </TwoFactorLinkButton>
      </div>
    </>
  );
}
